import {
  FormHandler,
  RelationEditor,
  showSuccessApplyMessage,
} from '@framework/form';
import { Drawer } from '@framework/ui';
import { yup } from '@framework/validation';
import { Button, ButtonProps, message } from 'antd';
import produce from 'immer';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TreeNavigatorLogic } from '../../../../../../tree/navigation/TreeNavigatorLogic';
import {
  TreeReferencesContextProvider,
  useTreeReferencesContext,
} from '../../../../../../tree/references/TreeReferencesContext';
import { DrawerEditorButtonRow } from '../../../../../../ui/drawer';
import { WidgetType } from '../../../../Widget';
import { WidgetSelect } from '../../../../select/WidgetSelect';
import { WidgetSelectFilters } from '../../../../select/WidgetSelectFilters';
import { useConfigValidationContext } from '../../../../validation/hooks/useConfigValidationContext';
import { WidgetRepository } from '../../../WidgetRepository';
import { WidgetConfigForm } from '../../../common/WidgetConfigForm';
import { IRootWidget } from '../../../containers/root/IRootWidget';
import { ITableColumnWidget, ITableInputWidget } from '../ITableInputWidget';
import { createTableWidgetInitialColumn } from './createTableWidgetInitialColumn';

const defaultValidationSchema = yup
  .object()
  .noUnknown() as yup.ObjectSchema<any>;

export interface ITableColumnWidgetEditDrawerProps {
  editor: RelationEditor<ITableColumnWidget>;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
}

export function TableColumnWidgetEditDrawer(
  props: ITableColumnWidgetEditDrawerProps,
) {
  const { editor, buttonProps } = props;

  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);

  const widgetType = WidgetRepository.find(selectedType);

  // Quando viene aperto il drawer in modifica impostiamo il tipo di widget selezionato
  // per caricare correttamente il form di configurazione.
  const currentWidgetType = WidgetRepository.find(editor.handle.current?.type);

  const validator = useMemo(
    () =>
      WidgetRepository.getConfigValidationSchema(editor.handle.current) ??
      defaultValidationSchema,
    [editor.handle],
  );

  const validationContext = useConfigValidationContext({ isDraft: true });

  const { root } = useTreeReferencesContext();
  const form = useFormContext<ITableInputWidget>();
  const [tableRoot, setTableRoot] = useState(root);

  // Con questo useEffect aggiorno l'albero delle reference ogni volta che viene aggiunto una nuova colonna
  // così da gestire la generazione automatica delle reference.
  useEffect(() => {
    const tableWidget = form.getValues();

    setTableRoot(
      produce(root, (draft: IRootWidget) => {
        if (!draft) return;

        // Recupero la tabella dall'albero
        const table = TreeNavigatorLogic.byId(
          draft,
          tableWidget.id,
        ) as ITableInputWidget | null;

        if (!table) return;

        // Aggiorno la tabella coi dati del form (e.g. modifiche non salvate alle righe per esempio)
        Object.assign(table, tableWidget);

        if (!editor.handle.isNew) return;

        // Creo una colonna fittizia per il widget che sto aggiungendo
        // In questo modo sarà possibile calcolare le reference a partire da lui stesso.
        // Altrimenti non essendo presente non avrei un "punto di partenza" da cui iniziare a calcolare le reference.
        table.columns = [
          ...editor.items,
          ...(editor.handle.current ? [editor.handle.current] : []),
        ];
      }),
    );
  }, [editor.handle, editor.items, form, root]);

  return (
    <>
      <WidgetSelect
        width="60%"
        filter={WidgetSelectFilters.TableColumn}
        onChange={value => {
          setSelectedType(value);
        }}
      />
      <Button
        children="+ Aggiungi Colonna"
        {...buttonProps}
        disabled={!widgetType}
        onClick={() => {
          if (!widgetType) return;

          const widget = createTableWidgetInitialColumn(widgetType.type, {
            label: 'Nuova colonna',
            code: `colonna_${editor.items.length}`,
          });

          if (!widget) return;

          editor.handle.open(widget);
        }}
      />
      <Drawer
        maskClosable={false}
        title="Configurazione Colonna"
        size="large"
        placement="right"
        onClose={editor.handle.cancel}
        open={editor.handle.isVisible}
        destroyOnClose
      >
        <TreeReferencesContextProvider
          root={tableRoot}
          widget={editor.handle.current!}
        >
          <FormHandler
            layout="vertical"
            schema={validator}
            initialValues={
              editor.handle.current ?? validator.cast({}, { assert: false })
            }
            validationContext={validationContext}
            onSubmit={async rawValues => {
              try {
                const values = await validator.validate(rawValues, {
                  context: validationContext,
                });

                const result = editor.handle.apply({
                  ...editor.handle.current,
                  ...values,
                });
                showSuccessApplyMessage(result, {
                  onAdd: 'Colonna aggiunta con successo.',
                  onUpdate: 'Colonna modificata con successo.',
                });
              } catch (e) {
                message.error('Si è verificato un errore nella gestione della colonna'); // prettier-ignore
                console.error(e);
              }
            }}
          >
            <WidgetConfigForm
              widget={editor.handle.current}
              widgetType={currentWidgetType}
              isTableColumn
            />
            <DrawerEditorButtonRow editor={editor} />
          </FormHandler>
        </TreeReferencesContextProvider>
      </Drawer>
    </>
  );
}
